import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

// The objects that fulfil one product, with their live state — the fleet
// behind the product-detail page's "Assigned objects" section.
//
// Old (Lambda) base query on purpose. The read reuses
// `liveHolderRentalItemWhere()` (backend/src/core/modules/common/asset/
// assetOccupancy.ts), which is the declared single source of truth for "is
// this object actually held by someone". The new backend has no equivalent —
// it uses a bare `is_active: true` (orderController.ts:1124), the narrower
// form that predicate was written to replace — so serving this from there
// would mean mirroring the rule and pinning the pair by a fixture (ADR-0014)
// for one read. This endpoint sits beside `depot/:depotId/assets` and
// `depot/:depotId/doors`, which are also still Lambda; the asset/depot surface
// has not been migrated.
//
// Deliberately NOT `depot/:depotId/doors`: that handler derives its door list
// from the IoT device shadow and throws when a depot has no `thing_id`
// (`getThingShadow`, shadow.ts:15), so it cannot serve a lock-free station —
// a boardrack, a manual storage bank, or any `door_mechanism: none` station,
// which is the wizard's default. It also answers per *station*, while a
// product may span several. Its own question — the physical `doorState` —
// is unaffected and still belongs to it.

/**
 * Reconciled object state. Three values, not four: `soonAvailable` appears in
 * several door-status unions but has no live producer — the only code that
 * ever set it is `backend/src/old_modules/resource.old/`, whose handlers are
 * all commented out in the slsconfigs. Typing a fourth value here would
 * promise a state the backend cannot deliver.
 */
export type FleetObjectState = 'available' | 'inUse' | 'blocked';

export interface FleetObjectAddress {
    street: string | null;
    houseNumber: string | null;
    postalCode: string | null;
    city: string | null;
    district: string | null;
}

export interface FleetObjectStation {
    id: string;
    name: string;
    /** A placeholder depot created by the room path, not a real cabinet. */
    isFictional: boolean;
}

export interface FleetObjectLocation {
    id: string;
    name: string;
    address: FleetObjectAddress | null;
}

/** Who holds the object right now — null unless it is genuinely occupied. */
export interface FleetObjectHolder {
    orderId: string | null;
    bookingNumber: string | null;
    /** ISO string; the backend stringifies it explicitly (ADR-0007 amendment). */
    endDate: string | null;
}

export interface FleetObject {
    assetId: string;
    name: string;
    description: string | null;
    floor: string | null;
    /** Already reconciled server-side — never the raw `asset.state` column. */
    state: FleetObjectState;
    /** Position numbers in the station (`doormapping.door`). */
    doors: number[];
    doorMechanisms: string[];
    station: FleetObjectStation | null;
    location: FleetObjectLocation | null;
    holder: FleetObjectHolder | null;
}

/**
 * One tariff row, in the same runtime shape `PriceScaleTable` already renders
 * for the asset detail and the Services price view — so the table is reused
 * rather than a third copy written. Decimals arrive as numbers and the
 * `@db.Time(6)` columns as ISO strings; the handler converts both explicitly.
 */
export interface ProductPriceScale {
    id: string;
    price: number | null;
    days: string | null;
    startTime: string | null;
    endTime: string | null;
    durationInMinutes: number | null;
    isFullDay: boolean;
    additionalDaysPrice: number | null;
}

export interface ProductPriceCategory {
    id: string;
    name: string;
    priceScales: ProductPriceScale[];
}

/**
 * The product header — the offering, as opposed to the physical objects.
 *
 * `null` when the product has no objects yet: the header is read off the
 * fleet's shared group, so with no rows there is nothing to read. Rendering a
 * blank header would be worse than saying the fleet is empty.
 */
export interface ProductHeader {
    productId: string;
    name: string | null;
    langNames: Record<string, { title: string, description: string }> | null;
    description: string | null;
    featuredImage: string | null;
    depositAmount: number | null;
    /** Raw `compartment_types`; compare case-insensitively. */
    useCases: string[];
    priceCategory: ProductPriceCategory | null;
}

export interface ProductFleetResponse {
    product: ProductHeader | null;
    objects: FleetObject[];
}

export const productObjectsApiSlice = createApi({
    reducerPath: 'productObjectsApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: axiosBaseQuery(),
    tagTypes: ['ProductFleet'],
    endpoints: (builder) => ({
        getProductFleet: builder.query<ProductFleetResponse, { groupId: string }>({
            query: ({ groupId }) => ({
                url: `/asset/group/${encodeURIComponent(groupId)}`,
                method: 'GET',
            }),
            providesTags: (result, error, { groupId }) => [
                { type: 'ProductFleet', id: groupId },
            ],
        }),

        // Take an object out of / back into service. Object-level facts, so
        // they are edited on the object surface and reflected in the fleet.
        //
        // The verb asymmetry (POST to block, GET to unblock) is the existing
        // API's, matched deliberately rather than guessed at — see
        // `asset.admin.slsconfig.yml`. Blocking an object that is currently
        // held is rejected by the service with a 400; the UI disables the
        // action, but the API is the enforcer (ADR-0020).
        blockAssetState: builder.mutation<unknown, { assetId: string, groupId: string }>({
            query: ({ assetId }) => ({
                url: `/asset/${encodeURIComponent(assetId)}/block`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, { groupId }) => [
                { type: 'ProductFleet', id: groupId },
            ],
        }),
        unblockAssetState: builder.mutation<unknown, { assetId: string, groupId: string }>({
            query: ({ assetId }) => ({
                url: `/asset/${encodeURIComponent(assetId)}/unblock`,
                method: 'GET',
            }),
            invalidatesTags: (result, error, { groupId }) => [
                { type: 'ProductFleet', id: groupId },
            ],
        }),
    }),
});

export const {
    useGetProductFleetQuery,
    useBlockAssetStateMutation,
    useUnblockAssetStateMutation,
} = productObjectsApiSlice;
